import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EMPTY, map, Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import {
  LOADING_INTERCEPTOR_TOKEN,
  SHOW_MODAL_ON_ERROR
} from '../../interceptors/interceptor.token';
import { LIB_CONFIG, LibConfig } from '../../lib.config';
import { BildungsangebotTerminDetail, Termin } from '../../model';
import { BildungsangebotPage } from '../../model/bildungsangebot-page';
import { RegionKurzKursnet } from '../../model/regionenliste';
import { checkParams } from '../../shared/utils/check-params.util';
import { LoaderService } from '../loader.service';
import { UrlService } from '../state/url.service';
import { BildungsBereich } from '../state/bildungsbereich.service';
import { SucheError } from './suche-error';
import { OrtService } from '../suchseite/ort.service';

export interface UmkreisParameter {
  ort?: string;
  uk?: string;
}

/**
 * Bildungsangebot Client Filter Kategorien,
 * welche eingesetzt werden, um die Abfragen an das Backend zu Filtern
 * Ein Eintrag einer neuen Filterkategorie muss auch unten in createHttpParamsFor mit nachgezogen werden
 */
export interface BildungsangebotFilterkategorien {
  dkzIds: number[];
  umkreis?: UmkreisParameter;
  anbieter: string[];
  region: RegionKurzKursnet[];
  unterrichtsformen: string[];
  unterrichtszeiten: string[];
  page?: number;
  beginntermine: string[];
  suchwort: string;
  kosten: string[];
  dauerKlassen: string[];
  ortsunabhaengig: boolean;

  /** TODO document me: was & wozu bin ich?
   *      scheinbar bin ich entgegen der anderen kein fachliches attribut
   */
  correctionState?: boolean;
  berufe?: number[];
  bildungsbereich?: BildungsBereich;
}

/**
 * Während BildungsangebotFilterkategorien alle Möglichkeiten abbilden,
 * wird hier für die HTTP-Übertragung ein partielles Interface angeboten,
 * ohne die Facetten.
 */
export interface BildungsangebotBasisFilterParameter extends Pick<
  BildungsangebotFilterkategorien,
  | 'dkzIds'
  | 'suchwort'
  | 'page'
  | 'umkreis'
  | 'ortsunabhaengig'
  | 'correctionState'
  | 'berufe'
  | 'bildungsbereich'
> {
  dkzIds: number[];
}

/**
 * URL-Parameter des Backends
 * diese können und sollten denen des Frontends gleichen, müssen dies aber nicht
 * @see UrlService
 */
export enum BildungsangebotServiceParams {
  DKZ_IDS = 'ids',
  UMKREIS = 'uk',
  ORT = 'ort',
  SEITE = 'page',
  SYSTEMATIK = 'sys',
  TESTSORTIERUNG = 'dazf',
  SUCHWORT = 'sw',
  DISABLE_CORRECTION = 'dac',
  ORTSUNABHAENGIG = 'ortsunabhaengig',
  IS_UTE = 'ute'
}

/**
 * Erzeugt die für die Backendanfrage nötigen HTTP Parameter
 * aus dem fachlichen Model der BildungsangebotHttpFilterParams
 */
export function createBasicHttpParamsFor(
  kriterien: Partial<BildungsangebotBasisFilterParameter>
): HttpParams {
  const httpParams = new HttpParams()
    .set(
      BildungsangebotServiceParams.DKZ_IDS,
      [...(kriterien.berufe ?? []), ...(kriterien.dkzIds ?? [])].join(UrlService.VALUE_SEPARATOR)
    )
    .set(BildungsangebotServiceParams.ORT, kriterien.umkreis?.ort ?? '')
    .set(
      BildungsangebotServiceParams.UMKREIS,
      kriterien.umkreis?.ort ? (kriterien.umkreis?.uk ?? OrtService.UMKREIS_VALUE_GANZER_ORT) : ''
    )
    .set(BildungsangebotServiceParams.ORTSUNABHAENGIG, kriterien.ortsunabhaengig ?? '')
    .set(BildungsangebotServiceParams.SEITE, kriterien.page ?? '')
    .set(
      BildungsangebotServiceParams.SUCHWORT,
      kriterien.berufe?.length == 0 //Kein Suchwort bei Berufssuche
        ? encodeURIComponent(kriterien.suchwort ?? '')
        : ''
    )
    .set(BildungsangebotServiceParams.IS_UTE, kriterien.bildungsbereich === 'erwerb')
    .set(BildungsangebotServiceParams.DISABLE_CORRECTION, kriterien.correctionState ?? '');
  return httpParams;
}

/**
 * Client kapselt die Zugriffe auf das Backend bzgl. Bildungsangeboten
 * Es können Listen oder einzelne Angebotsdetails abgerufen werden
 */
@Injectable({ providedIn: 'root' })
export class BildungsangebotClient {
  private readonly libConfig = inject<LibConfig>(LIB_CONFIG);
  private readonly http = inject(HttpClient);

  private readonly loaderService = inject(LoaderService);

  // TODO Rücksprache (CKU):
  //      Ist die Funktionssignatur mit HttpParams und einer exportierten Hilfsmethode zum Erstellen dieser
  //      aus dem hier angebotenen BildungsangebotHttpFilterParams als Partial auf einem zweiten Interface
  //      nicht unnötig kompliziert und offen und erschwert mit letzterem die Wartbarkeit (Informationhiding)?
  //      Wieso nicht auf BildungsangebotHttpFilterParams oder das volle BildungsangebotFilterkategorien umstellen?
  /**
   * Hole eine Liste (Seite) von Angeboten anhand der in die httpParams codierten Filtern und Paginierung.
   * Diese Seiten der Frontend-Backend-Kommunikation sind NICHT die gleichen wie in der paginierten Replikation
   */
  public getBildungsangeboteByDkz = (httpParams: HttpParams): Observable<BildungsangebotPage> =>
    this.http
      .get<BildungsangebotPage>(`${this.libConfig.backendHost}/pc/v1/bildungsangebot`, {
        params: checkParams(httpParams),
        responseType: 'json',
        context: new HttpContext()
          .set(SHOW_MODAL_ON_ERROR, true)
          .set(LOADING_INTERCEPTOR_TOKEN, 'angebot')
      })
      .pipe(
        catchError((error) => {
          if (error.status === 400) {
            return of(this.emptyPage);
          }
          throw new SucheError('Fehler beim Laden der Bildungsangebote', error);
        })
      );

  /**
   * Hole Details zu einem Angebot anhand seiner ID
   * @param id (Kursnet-)ID des Angebots, welche aus dem Basisdienst stammt
   */
  public getTerminById(id: string | number): Observable<BildungsangebotTerminDetail> {
    return this.http
      .get<BildungsangebotTerminDetail>(
        `${this.libConfig.backendHost}/pc/v1/bildungsangebot/${id}`,
        { responseType: 'json' }
      )
      .pipe(
        catchError((error) => {
          throw new SucheError('Fehler beim Laden des Termins', error);
        })
      );
  }

  public getCountsForSuchworte(
    suchworte: string[],
    isUte: boolean
  ): Observable<{ [key: string]: number }> {
    this.loaderService.setSuchwortCountsLoading(true);
    return this.http
      .post<{
        [key: string]: number;
      }>(
        `${this.libConfig.backendHost}/pc/v1/bildungsangebot/suchwortcounts`,
        { suchworte, isUte },
        { responseType: 'json', context: new HttpContext().set(SHOW_MODAL_ON_ERROR, true) }
      )
      .pipe(
        finalize(() => this.loaderService.setSuchwortCountsLoading(false)),
        catchError((error) => {
          if (error.status === 400) {
            return EMPTY;
          }
          throw new SucheError('Fehler beim Laden der suchwort counts', error);
        })
      );
  }

  public getTermineForAngebot(id: string | number, httpParams: HttpParams): Observable<Termin[]> {
    return this.http
      .get<{
        _embedded: { bildungsangebotTerminDTOList: Termin[] };
      }>(`${this.libConfig.backendHost}/pc/v1/bildungsangebot/${id}/termine`, {
        params: checkParams(httpParams),
        responseType: 'json',
        context: new HttpContext()
          .set(SHOW_MODAL_ON_ERROR, true)
          .set(LOADING_INTERCEPTOR_TOKEN, 'termine')
      })
      .pipe(
        map(({ _embedded }) => _embedded?.bildungsangebotTerminDTOList ?? []),
        catchError((error) => {
          if (error.status === 400) {
            return EMPTY;
          }
          throw new SucheError('Fehler beim Laden der Termin', error);
        })
      );
  }
  private get emptyPage(): BildungsangebotPage {
    return {
      page: { number: 0, size: 0, totalElements: 0, totalPages: 0 },
      _embedded: { bildungsangebotDTOList: [] }
    };
  }
}
